import React from 'react';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Briefcase, Zap, FileJson, Users, ArrowRight, ShieldCheck } from 'lucide-react';
import { SolutionHero } from '@/components/sections/SolutionHero';
import { PainPointsGrid } from '@/components/sections/PainPointsGrid';
import { trackSignupIntent } from '@/lib/tracking';

const ForConsultantsPage: React.FC = () => {
  const appSignUpUrl = import.meta.env.VITE_APP_URL 
    ? `${import.meta.env.VITE_APP_URL}/auth?mode=signup`
    : 'http://localhost:3000/auth?mode=signup';

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO 
        title="SWPPP Software for Environmental Consultants | RunOffTrack"
        description="Organize construction sites, complete field inspections, capture weather context, and generate signed PDF reports with RunOffTrack."
        canonical="/solutions/for-consultants"
      />

      <SolutionHero 
        badge="For Environmental Consultants"
        title={<span>Scale your firm,<br/><span className="text-primary">not your overhead.</span></span>}
        subtitle="Keep project sites, field findings, weather context, signatures, and completed reports in one inspection workflow."
        benefits={[
            "Manage project sites from one workspace",
            "Generate signed PDF inspection reports",
            "Capture current and recent weather context"
        ]}
        ctaText="Start Scaling Today"
        image={
            <div className="relative aspect-[4/3] bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-slate-700 p-6 overflow-hidden flex flex-col">
                {/* Mockup UI: Dashboard */}
                <div className="flex items-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-red-500"/>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"/>
                    <div className="w-3 h-3 rounded-full bg-green-500"/>
                </div>
                <div className="flex-1 space-y-4">
                    <div className="bg-slate-700/50 h-8 w-1/3 rounded mb-4"/>
                    {[1, 2, 3].map(i => (
                        <div key={i} className="bg-slate-700/30 p-4 rounded-xl flex justify-between items-center">
                            <div>
                                <div className="h-4 w-32 bg-slate-600 rounded mb-2"/>
                                <div className="h-3 w-20 bg-slate-600/50 rounded"/>
                            </div>
                            <div className={`px-3 py-1 rounded text-xs font-bold ${i===1 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                                {i===1 ? 'Action Required' : 'Compliant'}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="absolute bottom-0 right-0 p-6 opacity-10">
                    <Briefcase size={120} className="text-white"/>
                </div>
            </div>
        }
      />

      <PainPointsGrid 
        title="Stop giving away billable hours."
        subtitle="Manual administrative tasks are eating your margins. It's time to automate."
        points={[
            {
                icon: Zap,
                problem: "Chasing Weather Data",
                solution: "Load recent rainfall and forecast context for a geolocated site when an inspection begins."
            },
            {
                icon: FileJson,
                problem: "Word Doc Formatting",
                solution: "Generate a consistent PDF report after completing findings, notes, and the inspector signature."
            },
            {
                icon: Users,
                problem: "Client Emails",
                solution: "Send the completed report to the project contact saved for the site when delivery is configured."
            }
        ]}
      />

      {/* Feature Deep Dive */}
      <section className="py-24">
        <div className="container px-4 max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
                <div className="order-2 md:order-1">
                    <div className="inline-flex items-center justify-center p-3 bg-primary/10 text-primary rounded-xl mb-6">
                        <ShieldCheck size={24} />
                    </div>
                    <h2 className="text-3xl font-bold mb-4">Consistent, Signed Reports</h2>
                    <p className="text-lg text-muted-foreground mb-6">
                        Move from field observations to a downloadable report without rebuilding the document by hand.
                    </p>
                    <ul className="space-y-3">
                        {[
                            "BMP findings and corrective-action notes",
                            "Inspection weather context",
                            "Inspector name and captured signature",
                            "Downloadable report history by project"
                        ].map((item, i) => (
                            <li key={i} className="flex items-center gap-3">
                                <CheckCircle2 size={18} className="text-green-500 shrink-0"/>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="order-1 md:order-2 bg-muted rounded-3xl aspect-square flex items-center justify-center border border-border">
                    <div className="text-center">
                        <div className="w-32 h-10 bg-white rounded shadow-sm mx-auto mb-4 flex items-center justify-center font-bold text-muted-foreground border border-border">
                            SITE REPORT
                        </div>
                        <div className="w-48 h-64 bg-white shadow-xl mx-auto border border-border rounded-sm relative p-4 text-left">
                            <div className="w-8 h-8 bg-gray-100 rounded-full mb-4"></div>
                            <div className="h-2 w-24 bg-gray-200 rounded mb-2"></div>
                            <div className="h-2 w-32 bg-gray-100 rounded mb-6"></div>
                            <div className="h-20 bg-blue-50 border border-blue-100 rounded mb-2"></div>
                            <div className="h-20 bg-green-50 border border-green-100 rounded"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-primary text-primary-foreground text-center">
        <div className="container px-4">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to run your first digital inspection?</h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto mb-10">
                Start with one active project and unlimited inspections. No credit card required.
            </p>
            <Button size="lg" variant="secondary" className="h-14 px-8 rounded-full text-lg" asChild>
                <a href={appSignUpUrl} onClick={() => trackSignupIntent('consultants_bottom_cta')}>Get Started for Free <ArrowRight className="ml-2"/></a>
            </Button>
        </div>
      </section>
    </div>
  );
};

export default ForConsultantsPage;
